import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, AlertCircle, Sparkles, TrendingUp, Heart } from "lucide-react";

export const Route = createFileRoute("/admin/dev/progress-mockup")({
  component: ProgressMockup,
});

type Version = 1 | 2 | 3;

const DATA: Record<Version, {
  addressed: number;
  total: number;
  improved: string[];
  remaining: string[];
  encouragement: string;
}> = {
  1: {
    addressed: 0,
    total: 7,
    improved: [],
    remaining: [
      "Thesis statement is vague — sharpen the central claim",
      "Citations missing for three empirical claims in §2",
      "Topic sentences absent from paragraphs 4, 6, 9",
      "Conclusion restates rather than synthesises",
      "Voice slips into first-person in §3",
      "Reference list ordering inconsistent with Harvard",
      "Signposting between sections is thin",
    ],
    encouragement:
      "Good first draft — there's a real argument here. The next pass is about making that argument visible to the reader.",
  },
  2: {
    addressed: 4,
    total: 7,
    improved: [
      "Thesis is now crisp and arguable — well done",
      "All §2 empirical claims now cited",
      "Topic sentences added to ¶4 and ¶6",
      "Reference list reordered correctly",
    ],
    remaining: [
      "Topic sentence still missing from ¶9",
      "Conclusion still summarises rather than synthesises",
      "One first-person slip remains in §3",
    ],
    encouragement:
      "Strong progress — over half the issues resolved, and the structural ones at that. The remaining three are smaller and well within reach.",
  },
  3: {
    addressed: 7,
    total: 7,
    improved: [
      "Every issue from the previous version addressed",
      "Conclusion now synthesises and points forward",
      "Voice is consistently academic throughout",
      "Signposting between sections is clean and confident",
    ],
    remaining: [],
    encouragement:
      "This is excellent work. The argument is sharp, the structure is sound, and the voice is yours. Be proud of this draft.",
  },
};

function ProgressMockup() {
  const [version, setVersion] = useState<Version>(2);
  const d = DATA[version];
  const pct = Math.round((d.addressed / d.total) * 100);
  const isPerfect = version === 3;

  return (
    <div className="space-y-8 max-w-3xl">
      <header>
        <p className="overline mb-2">Dev · mockup</p>
        <h2 className="font-serif text-2xl text-primary">Incremental progress</h2>
        <p className="text-ink-secondary text-sm mt-2">
          A simulated view of how a student sees their revision journey across versions. Static data — not wired to any review.
        </p>
        <p className="text-xs font-mono text-ink-tertiary mt-2">
          <Link to="/admin" className="hover:text-primary">Admin</Link>
          {" / Dev / Progress mockup"}
        </p>
      </header>

      {/* Version selector */}
      <div className="inline-flex rounded-full border border-rule p-1 bg-card">
        {([1, 2, 3] as Version[]).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVersion(v)}
            className={`px-4 py-1.5 text-sm font-mono rounded-full transition-colors ${
              version === v
                ? "bg-primary text-primary-foreground"
                : "text-ink-secondary hover:text-primary"
            }`}
          >
            V{v}
          </button>
        ))}
      </div>

      {/* Progress overview */}
      <section className="border border-border bg-card p-6 space-y-4 relative">
        {isPerfect && (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-500/15 dark:text-yellow-200 border border-yellow-300/60">
            <Sparkles className="h-3 w-3" /> Practically perfect
          </span>
        )}
        <div className="flex items-center gap-2 text-ink-secondary text-xs font-mono uppercase tracking-wider">
          <TrendingUp className="h-3.5 w-3.5" /> Progress
        </div>
        <p className="font-serif text-lg text-primary">
          {version === 1
            ? `${d.total} issues identified in this first draft`
            : `You've addressed ${d.addressed} of ${d.total} issues from the previous version`}
        </p>
        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${version === 1 ? 0 : pct}%` }}
          />
        </div>
        <p className="text-xs font-mono text-ink-tertiary">
          {version === 1 ? "Baseline" : `${pct}% of previous issues resolved`}
        </p>
      </section>

      {/* What improved */}
      {d.improved.length > 0 && (
        <section className="border border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-500/5 dark:border-emerald-500/30 p-6 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-xs font-mono uppercase tracking-wider">
            <CheckCircle2 className="h-3.5 w-3.5" /> What improved
          </div>
          <ul className="space-y-2">
            {d.improved.map((line, i) => (
              <li key={i} className="flex gap-3 font-serif text-ink-primary">
                <CheckCircle2 className="h-4 w-4 mt-1 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Still needs attention */}
      {d.remaining.length > 0 && (
        <section className="border border-border bg-card p-6 space-y-3">
          <div className="flex items-center gap-2 text-ink-secondary text-xs font-mono uppercase tracking-wider">
            <AlertCircle className="h-3.5 w-3.5" /> Still worth a look
          </div>
          <ul className="space-y-2">
            {d.remaining.map((line, i) => (
              <li key={i} className="flex gap-3 font-serif text-ink-primary">
                <span className="h-4 w-4 mt-1 shrink-0 rounded-full border border-ink-tertiary" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Encouragement */}
      <section
        className={`border p-6 flex gap-3 ${
          version === 3
            ? "border-yellow-300/60 bg-yellow-50/60 dark:bg-yellow-500/5 dark:border-yellow-500/30"
            : version === 2
            ? "border-primary/30 bg-primary/5"
            : "border-border bg-muted/40"
        }`}
      >
        <Heart
          className={`h-5 w-5 mt-0.5 shrink-0 ${
            version === 3 ? "text-yellow-600 dark:text-yellow-300" : "text-primary"
          }`}
        />
        <p className="font-serif text-base text-ink-primary leading-relaxed">
          {d.encouragement}
        </p>
      </section>
    </div>
  );
}
